import React from "react";
import PropTypes from "prop-types";
import styles from "./UserFilters.module.css";

/**
 * UserFilters - Component for filtering users data
 *
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Callback when a filter changes
 * @param {Function} props.onApplyFilters - Callback when filters should be applied
 * @param {Array} props.predefinedFilters - List of predefined filter sets
 * @param {boolean} props.isLoading - Whether data is currently loading
 */
const UserFilters = ({
  filters,
  onFilterChange,
  onApplyFilters,
  onApplyPredefinedFilter,
  predefinedFilters,
  isLoading,
}) => {
  // Handle individual filter change
  const handleChange = (name, value) => {
    onFilterChange(name, value);
  };

  return (
    <div className={styles.filterSection}>
      <h3>Filters</h3>

      <div className={styles.filterControls}>
        <div className={styles.filterGroup}>
          <label htmlFor="daysBack">Days Back:</label>
          <input
            type="number"
            id="daysBack"
            value={filters.daysBack || ""}
            onChange={(e) =>
              handleChange(
                "daysBack",
                e.target.value ? parseInt(e.target.value, 10) : ""
              )
            }
            placeholder="Days"
          />
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="sex">Sex:</label>
          <select
            id="sex"
            value={filters.sex || ""}
            onChange={(e) => handleChange("sex", e.target.value)}
          >
            <option value="">All</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label htmlFor="hasGroupMeId">GroupMe ID:</label>
          <select
            id="hasGroupMeId"
            value={
              filters.hasGroupMeId === undefined
                ? ""
                : filters.hasGroupMeId
                ? "has"
                : "missing"
            }
            onChange={(e) => {
              const value = e.target.value;
              handleChange(
                "hasGroupMeId",
                value === "has" ? true : value === "missing" ? false : undefined
              );
            }}
          >
            <option value="">Any</option>
            <option value="has">Has GroupMe ID</option>
            <option value="missing">Missing GroupMe ID</option>
          </select>
        </div>

        <button
          className={styles.applyFilterBtn}
          onClick={onApplyFilters}
          disabled={isLoading}
        >
          {isLoading ? "Loading..." : "Apply Filters"}
        </button>
      </div>

      {predefinedFilters && predefinedFilters.length > 0 && (
        <div className={styles.favoriteFilters}>
          <h4>Saved Filters</h4>
          <div className={styles.filterButtons}>
            {predefinedFilters.map((filter, index) => (
              <button
                key={index}
                className={styles.filterBtn}
                onClick={() => onApplyPredefinedFilter(filter)}
                disabled={isLoading}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

UserFilters.propTypes = {
  filters: PropTypes.shape({
    daysBack: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sex: PropTypes.string,
    hasGroupMeId: PropTypes.bool,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }),
  onFilterChange: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onApplyPredefinedFilter: PropTypes.func.isRequired,
  predefinedFilters: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      filters: PropTypes.object.isRequired,
    })
  ),
  isLoading: PropTypes.bool,
};

UserFilters.defaultProps = {
  filters: {},
  predefinedFilters: [],
  isLoading: false,
};

export default UserFilters;
